/* eslint-disable  @typescript-eslint/no-explicit-any */
import { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@webapp/auth/services/auth.service';
import {
  IS_PUBLIC,
  RETRIED_AFTER_REFRESH,
  SKIP_AUTO_LOGOUT,
  SKIP_CONFLICT_REDIRECT,
  SKIP_ERROR_TOAST,
  SKIP_FORBIDDEN_LOGOUT,
} from '@webapp/auth/interceptors/auth.interceptor';
import { ToastService } from '@webapp/ui/components/toast/toast.service';
import { catchError, firstValueFrom, Observable, switchMap, throwError } from 'rxjs';

export const UNAUTHORIZED_REDIRECT_URL = new InjectionToken<string>('UNAUTHORIZED_REDIRECT_URL', {
  providedIn: 'root',
  factory: () => '/ingresar',
});
import { ERRORS } from '../texts';

export const HttpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const toastService = inject(ToastService);
  const unauthorizedRedirectUrl = inject(UNAUTHORIZED_REDIRECT_URL);

  // Dispatches `request` and, on error, routes it through `resolveError()`.
  // Recursive by design: the single retry-after-refresh attempt below
  // re-enters this same function with the retried (RETRIED_AFTER_REFRESH-
  // tagged) request, so a failure on the RETRY itself gets the exact same
  // toast/logout handling as any other error — it just can no longer
  // re-attempt a refresh (see the RETRIED_AFTER_REFRESH check below).
  const dispatch = (request: HttpRequest<any>): Observable<HttpEvent<any>> =>
    next(request).pipe(
      catchError((error: any) => resolveError(error, request))
    );

  const resolveError = (
    error: any,
    request: HttpRequest<any>
  ): Observable<HttpEvent<any>> => {
    // IS_PUBLIC requests (login, refreshToken, verifyMagicLink, sendMagicLink,
    // registerProspect/registerClient — see AuthService's `this.CONTEXT`) are
    // expected to legitimately 401/403 when there is no session yet (e.g. the
    // bootstrap silent refresh for an anonymous visitor). That is not an
    // authenticated-session-expired condition, so it must NOT trigger
    // logout()+redirect — doing so would force-navigate anonymous users away
    // from whatever page/deep-link they landed on. Genuine protected requests
    // are never tagged IS_PUBLIC, so this exemption does not weaken the
    // existing redirect-on-expired-session behavior anywhere else.
    //
    // SKIP_AUTO_LOGOUT requests (session-revoke: DELETE auth/sessions/:sid,
    // POST auth/sessions/revoke-others) DO require an authenticated session,
    // but a 403 from them means "not your session," not "your session is
    // invalid" — so the forced logout/redirect must be skipped there too,
    // letting the calling component's own error handling react to the
    // response instead of the user being redirected out from under it.
    const isAuthError = error.status === 401 || error.status === 403;
    // SKIP_FORBIDDEN_LOGOUT bypasses ONLY a 403 (API keys management); a 401
    // on the same request keeps the usual refresh/logout flow.
    const isAuthBypassed =
      request.context.get(IS_PUBLIC) ||
      request.context.get(SKIP_AUTO_LOGOUT) ||
      (error.status === 403 && request.context.get(SKIP_FORBIDDEN_LOGOUT));

    // Retry-after-refresh safety net (PR4): a REAL 401 from the backend (not
    // the proactive exp-based check in authInterceptor) may just mean the
    // access token was revoked/clock-skewed while the refresh token is still
    // good — attempt one silent refresh + replay before forcing a logout.
    // Three anti-loop layers guard this:
    //   1. RETRIED_AFTER_REFRESH context token — at most one retry per
    //      original request, set on the clone before replaying it.
    //   2. AuthService.refreshRejected() latch — once the backend has
    //      explicitly rejected the refresh token, don't attempt again until
    //      a new login/refresh clears it.
    //   3. AuthService.refreshToken()'s own refreshInFlight$ dedupe — even if
    //      several requests 401 concurrently, only one refresh call reaches
    //      the backend; every retry shares its outcome.
    // 403 is deliberately excluded: it's an authorization failure, not an
    // authentication one — refreshing the access token cannot fix it.
    const alreadyRetried = request.context.get(RETRIED_AFTER_REFRESH);
    const canRetryAfterRefresh =
      error.status === 401 &&
      !isAuthBypassed &&
      !alreadyRetried &&
      !authService.refreshRejected();

    if (canRetryAfterRefresh) {
      return authService.refreshToken().pipe(
        switchMap(() => {
          if (!authService.isAuthenticated()) {
            return finalizeError(error, request, isAuthError, isAuthBypassed);
          }
          const retriedReq = request.clone({
            context: request.context.set(RETRIED_AFTER_REFRESH, true),
            headers: request.headers.set(
              'Authorization',
              `Bearer ${authService.accessToken()}`
            ),
          });
          return dispatch(retriedReq);
        })
      );
    }

    return finalizeError(error, request, isAuthError, isAuthBypassed);
  };

  const finalizeError = (
    error: any,
    request: HttpRequest<any>,
    isAuthError: boolean,
    isAuthBypassed: boolean
  ): Observable<HttpEvent<any>> => {
    if (isAuthError && !isAuthBypassed) {
      // Fire-and-forget: navigation must happen regardless of logout()'s outcome,
      // so it does not block or depend on the error propagation below.
      void (async () => {
        try {
          await firstValueFrom(authService.logout());
        } catch {
          // logout() failing client-side must not prevent redirecting to login.
        }
        router.navigateByUrl(unauthorizedRedirectUrl);
      })();
    }

    if (error.error instanceof Blob) {
      return throwError(() => error);
    }

    const errorMessage: any = new Error();
    errorMessage.name = error.error?.title ?? 'Ocurrió un error.';
    errorMessage.cause = {
      errors: error.error?.error?.detail ?? error.error?.detail ?? error.message,
    };

    if (error.status === 409 && !request.context.get(SKIP_CONFLICT_REDIRECT)) {
      router.navigate(['error'], {
        state: {
          message: ERRORS.MERCADO_PAGO.PAYMENT_PENDING.MESSAGE,
          type: 'warning',
          title: ERRORS.MERCADO_PAGO.PAYMENT_PENDING.TITLE
        }
      });
    }

    // Auth-bypassed requests (silent refresh, login, magic-link, ...) that fail
    // with 401/403 already handle their own user-facing feedback (or, in the
    // silent-refresh case, deliberately show none) — see the comment above.
    // SKIP_ERROR_TOAST-tagged requests (login/MFA/client-login inline flows)
    // suppress the toast independent of status, since the calling component
    // already renders its own inline error message for any failure.
    const skipErrorToast = request.context.get(SKIP_ERROR_TOAST);
    const suppressToast = skipErrorToast || (isAuthError && isAuthBypassed);

    if (!suppressToast) {
      toastService.showToast(
        errorMessage.name,
        errorMessage.cause.errors,
        'error',
        true
      );
    }

    return throwError(() => error);
  };

  return dispatch(req);
};
